import { useEffect } from "react";

type CapacitorModule = {
	Capacitor: {
		isNativePlatform: () => boolean;
		getPlatform: () => string;
	};
};

type PushNotification = {
	title?: string;
	body?: string;
	data: Record<string, string>;
};

type PushNotificationActionPerformed = {
	notification: PushNotification;
	actionId: string;
};

type PushNotificationsModule = {
	PushNotifications: {
		requestPermissions: () => Promise<{ receive: string }>;
		register: () => Promise<void>;
		addListener: {
			(
				event: "registration",
				cb: (token: { value: string }) => void,
			): Promise<{ remove: () => void }>;
			(
				event: "registrationError",
				cb: (error: unknown) => void,
			): Promise<{ remove: () => void }>;
			(
				event: "pushNotificationReceived",
				cb: (notification: PushNotification) => void,
			): Promise<{ remove: () => void }>;
			(
				event: "pushNotificationActionPerformed",
				cb: (action: PushNotificationActionPerformed) => void,
			): Promise<{ remove: () => void }>;
		};
	};
};

const NON_WORKSPACE_SEGMENTS = new Set([
	"",
	"sign-in",
	"sign-up",
	"workspaces",
	"admin",
	"invite",
]);

function getWorkspaceSlug(): string | undefined {
	const firstSegment = window.location.pathname.split("/")[1] ?? "";
	if (NON_WORKSPACE_SEGMENTS.has(firstSegment)) return undefined;
	return firstSegment || undefined;
}

export function usePushNotifications(): void {
	useEffect(() => {
		let cleanup: (() => void) | undefined;

		(async () => {
			try {
				const coreMod = "@capacitor/core";
				const pushMod = "@capacitor/push-notifications";
				const { Capacitor } = (await import(
					/* @vite-ignore */ coreMod
				)) as CapacitorModule;
				if (!Capacitor.isNativePlatform()) return;

				const { PushNotifications } = (await import(
					/* @vite-ignore */ pushMod
				)) as PushNotificationsModule;

				const permResult = await PushNotifications.requestPermissions();
				if (permResult.receive !== "granted") return;

				await PushNotifications.register();

				const listener = await PushNotifications.addListener(
					"registration",
					(token: { value: string }) => {
						const workspaceSlug = getWorkspaceSlug();
						if (!workspaceSlug) return;

						const platform = Capacitor.getPlatform();
						fetch(`/api/tenant/${workspaceSlug}/push-tokens`, {
							method: "POST",
							headers: { "Content-Type": "application/json" },
							body: JSON.stringify({
								token: token.value,
								platform,
							}),
						}).catch(() => {});
					},
				);

				const errorListener = await PushNotifications.addListener(
					"registrationError",
					() => {},
				);

				const receivedListener = await PushNotifications.addListener(
					"pushNotificationReceived",
					() => {
						// Foreground notification — no action needed.
						// The FCM payload contains only title/body with no data fields.
					},
				);

				const actionListener = await PushNotifications.addListener(
					"pushNotificationActionPerformed",
					(action) => {
						const url = action.notification.data?.url;
						if (url) {
							window.location.href = url;
						}
					},
				);

				cleanup = () => {
					listener.remove();
					errorListener.remove();
					receivedListener.remove();
					actionListener.remove();
				};
			} catch {
				// Not in a Capacitor environment — no-op in browsers
			}
		})();

		return () => {
			cleanup?.();
		};
	}, []);
}
